import React, { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { Bell, BellOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  isPushSupported,
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribedToPush,
} from "@/services/pushNotifications";

const PushNotificationToggle = () => {
  const { token } = useAuth();
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const checkStatus = async () => {
      setSupported(isPushSupported());
      if (isPushSupported()) {
        const isSubbed = await isSubscribedToPush();
        setSubscribed(isSubbed);
      }
    };
    checkStatus();
  }, []);

  const handleToggle = async () => {
    setLoading(true);
    try {
      if (subscribed) {
        await unsubscribeFromPush(token);
        setSubscribed(false);
        toast.success("Push notifications disabled");
      } else {
        const granted = await requestNotificationPermission();
        if (granted) {
          const subscription = await subscribeToPush(token);
          if (subscription) {
            setSubscribed(true);
            toast.success("Push notifications enabled!");
          } else {
            toast.error("Failed to enable push notifications");
          }
        } else {
          toast.error("Notification permission denied");
        }
      }
    } catch (error) {
      console.error("Toggle push error:", error);
      toast.error("Failed to toggle push notifications");
    } finally {
      setLoading(false);
    }
  };

  if (!supported) {
    return (
      <div className="flex items-center gap-2 text-gray-500 text-sm">
        <BellOff size={16} />
        <span>Push notifications not supported</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <Switch
        id="push-notifications"
        checked={subscribed}
        onCheckedChange={handleToggle}
        disabled={loading}
      />
      <Label
        htmlFor="push-notifications"
        className="flex items-center gap-2 cursor-pointer"
      >
        {subscribed ? (
          <>
            <Bell size={16} className="text-green-500" />
            <span className="text-sm">Push notifications on</span>
          </>
        ) : (
          <>
            <BellOff size={16} className="text-gray-400" />
            <span className="text-sm text-gray-500">Enable push notifications</span>
          </>
        )}
      </Label>
    </div>
  );
};

export default PushNotificationToggle;
